import { Filter, GitMerge, Search, SlidersHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  FilterBuilder,
  TimeRangePicker,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';

/** A column def whose row shape is left open. */
export type SectionColumn<Row> = ColumnDef<Row, unknown>;

/** Resolve a column's stable toggle key (id, falling back to a string header). */
export const colId = <Row,>(c: SectionColumn<Row>): string =>
  typeof c.id === 'string' ? c.id : String(c.header ?? '');

export interface InvoiceListToolbarProps<Row> {
  columns: SectionColumn<Row>[];
  filterFields?: FilterField[];
  searchPlaceholder: string;
  query: string;
  onQueryChange: (value: string) => void;
  conditions: FilterCondition[];
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  onApplyFilters: (next: FilterCondition[]) => void;
  /** Applied-date range (`invoice_apply_startdate`), datetime strings. */
  dateFrom?: string;
  dateTo?: string;
  onDateRangeApply: (from?: string, to?: string) => void;
  visible: Record<string, boolean>;
  onToggleColumn: (id: string, checked: boolean) => void;
  /** Number of currently selected rows (drives the Merge button). */
  selectedCount: number;
  onMerge: () => void;
}

/**
 * Invoice list toolbar: a server-side search `Input` (start) plus a Merge
 * action, the Filters `Popover` → `FilterBuilder`, and a Display-options
 * column-visibility `DropdownMenu` (end). Purely presentational — all state
 * lives in the parent `InvoiceListSection`. Mirrors the contract toolbar, adding
 * the selection-driven Merge button.
 */
export function InvoiceListToolbar<Row>({
  columns,
  filterFields,
  searchPlaceholder,
  query,
  onQueryChange,
  conditions,
  filterOpen,
  onFilterOpenChange,
  onApplyFilters,
  dateFrom,
  dateTo,
  onDateRangeApply,
  visible,
  onToggleColumn,
  selectedCount,
  onMerge,
}: InvoiceListToolbarProps<Row>) {
  // Conditions fold into a `{ field: [...] }` filters object, so the badge counts
  // DISTINCT constrained fields, not the raw condition count.
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          startSlot={<Search size={15} />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          <Button
            variant="secondary"
            startIcon={<GitMerge size={15} />}
            disabled={selectedCount < 2}
            onClick={onMerge}
            title={
              selectedCount < 2
                ? 'Select at least two invoices to merge'
                : `Merge ${selectedCount} invoices`
            }
          >
            Merge{selectedCount ? <Badge tone="primary">{selectedCount}</Badge> : null}
          </Button>

          <TimeRangePicker
            showLive={false}
            presets={[]}
            from={dateFrom}
            to={dateTo}
            triggerLabel={
              dateFrom && dateTo
                ? `${dateFrom.slice(0, 10)} → ${dateTo.slice(0, 10)}`
                : 'Applied date'
            }
            onApply={(v) => onDateRangeApply(v.from, v.to)}
          />

          {filterFields?.length ? (
            <Popover open={filterOpen} onOpenChange={onFilterOpenChange}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{' '}
                  {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={onApplyFilters}
                  onClose={() => onFilterOpenChange(false)}
                />
              </PopoverContent>
            </Popover>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {columns.map((c) => {
                const id = colId(c);
                return (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={visible[id] !== false}
                    onCheckedChange={(v) => onToggleColumn(id, v === true)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {typeof c.header === 'string' ? c.header : id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    />
  );
}
